import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronsUpDown, Music, Send, Trash2, Upload } from "lucide-react";
import { MasterShell } from "@/components/master-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  useNotificationSounds,
  useUploadNotificationSound,
  useDeleteNotificationSound,
  useTestPushDevices,
  useSendTestPush,
} from "@/hooks/queries/platform";
import { ALERT_TYPE_OPTIONS, type AlertType } from "@/services/alert-rules.service";
import { PlatformApiError } from "@/lib/api/gpsdrive-platform-client";
import { cn } from "@/lib/utils";
import { TECHNICIAN_ASSIGNED_SOUND_KEY } from "@/components/technician-app/tech-new-assignment";
import {
  DEFAULT_MAINTENANCE_SERVICE_NAMES,
  MAINTENANCE_SOUND_KEY,
  maintenanceSoundKey,
} from "@/lib/fleet-maintenance-sounds";

export const Route = createFileRoute("/master/notifications")({
  component: () => (
    <MasterShell>
      <NotificationSoundsPage />
    </MasterShell>
  ),
  head: () => ({ meta: [{ title: "Notificações — Super Master — GPS Drive" }] }),
});

function NotificationSoundsPage() {
  const { data: sounds, isLoading } = useNotificationSounds();
  const soundByKey = new Map((sounds ?? []).map((s) => [s.alertKey, s]));

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Notificações</h1>
        <p className="text-sm text-muted-foreground">
          Som tocado quando o app de qualquer empresa está aberto e um alerta desse tipo dispara.
          Vale para toda a plataforma — não é configurável por empresa.
        </p>
      </div>

      <Card className="border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/20">
        <CardContent className="pt-6 text-sm text-amber-800 dark:text-amber-300">
          Celulares e navegadores não deixam nenhum app escolher um som diferente por tipo de
          notificação quando o app está fechado — o som do push é sempre o padrão do aparelho, sem
          exceção. O arquivo enviado aqui só toca com o app aberto, no momento em que o alerta chega.
        </CardContent>
      </Card>

      <TestPushCard />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tipos de alerta</CardTitle>
          <CardDescription>
            {isLoading ? "Carregando…" : "Envie um arquivo de áudio (mp3, wav ou ogg) para cada tipo."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {ALERT_TYPE_OPTIONS.map((option) => (
            <SoundSlot key={option.key} alertKey={option.key} label={option.label} emoji={option.emoji} current={soundByKey.get(option.key)} />
          ))}
        </CardContent>
      </Card>

      {/* Pedido 2026-10-09: aviso do app do técnico (/tecnico) quando um
          atendimento da Agenda é atribuído a ele — ver tech-new-assignment.tsx. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Agenda — app do técnico</CardTitle>
          <CardDescription>
            Toca no app do técnico quando um atendimento novo é atribuído a ele. Sem arquivo, toca um bipe.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <SoundSlot
            alertKey={TECHNICIAN_ASSIGNED_SOUND_KEY}
            label="Novo atendimento atribuído"
            emoji="🛠️"
            current={soundByKey.get(TECHNICIAN_ASSIGNED_SOUND_KEY)}
          />
        </CardContent>
      </Card>

      {/* Pedido 2026-10-14: um som para cada manutenção (troca de óleo,
          pneus…) — vai no push do app do celular quando o aviso da próxima
          manutenção sai (ver fleet-maintenance-notifier.ts no backend). */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Gestão de Frota — avisos de manutenção</CardTitle>
          <CardDescription>
            Som da notificação no celular quando chega a hora de cada manutenção. Serviço sem som próprio usa o de
            &quot;Todas as manutenções&quot;; sem nenhum, toca o som padrão do aparelho.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <SoundSlot
            alertKey={MAINTENANCE_SOUND_KEY}
            label="Todas as manutenções (padrão)"
            emoji="🔧"
            current={soundByKey.get(MAINTENANCE_SOUND_KEY)}
          />
          {DEFAULT_MAINTENANCE_SERVICE_NAMES.map((name) => {
            const key = maintenanceSoundKey(name);
            return <SoundSlot key={key} alertKey={key} label={name} emoji="🛠️" current={soundByKey.get(key)} />;
          })}
        </CardContent>
      </Card>
    </div>
  );
}

// Botão "Enviar teste" (2026-09-14) — manda um push FCM de verdade (tipo
// vibração, único com botões de ação nativos: Ver mapa/Dispensar alarme,
// ver GpsDriveMessagingService.java no app Android) pro(s) usuário(s)
// autorizados no veículo escolhido, sem precisar esperar um evento real de
// vibração acontecer. O push só entrega pra quem tem o app instalado E
// autorizado a ver aquele veículo específico — não existe "notificação de
// teste" solta sem dono, por isso o seletor é por veículo, não por
// empresa/usuário.
//
// Pedido 2026-09-14 ("o id do veículo que ele pede é o quê? tem que
// listar os veículos") — antes disto era um campo de texto pro admin
// digitar o ID cru do Traccar de cabeça. Lista TODA a frota da plataforma
// (todas as empresas, ver GET /test-push/devices no backend) — com a
// frota grande (5000+ devices em produção), o Command de baixo NUNCA
// renderiza a lista inteira: só mostra resultado depois de pelo menos 2
// caracteres digitados, e corta em 50 itens mesmo com mais match — é só
// um seletor de teste interno, não uma lista pra navegar inteira.
function TestPushCard() {
  const { data: devices, isLoading: devicesLoading } = useTestPushDevices();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<{ id: number; name: string } | null>(null);
  const sendTestPush = useSendTestPush();

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (term.length < 2) return [];
    return (devices ?? []).filter((d) => d.name.toLowerCase().includes(term)).slice(0, 50);
  }, [devices, search]);

  const handleSend = () => {
    if (!selected) {
      toast.error("Escolha um veículo");
      return;
    }
    sendTestPush.mutate(selected.id, {
      onSuccess: (result) => {
        if (result.tokensNotified === 0) {
          toast.warning("Nenhum aparelho encontrado", {
            description:
              "Esse veículo não tem nenhum usuário autorizado com o app Android aberto/logado " +
              "(token de notificação registrado).",
          });
          return;
        }
        toast.success(`Enviado para ${result.tokensNotified} aparelho(s)`, {
          description: result.usedRealRule
            ? "Usando o alerta de vibração real desse veículo — \"Dispensar alarme\" vai silenciá-lo de verdade."
            : "Esse veículo não tem um alerta de vibração configurado — \"Dispensar alarme\" só aparece, sem efeito.",
        });
      },
      onError: (err) =>
        toast.error("Não foi possível enviar o teste", {
          description: err instanceof PlatformApiError ? err.message : undefined,
        }),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Testar notificação nativa</CardTitle>
        <CardDescription>
          Envia uma notificação de teste (tipo "Alerta de vibração", com os botões "Ver mapa" e
          "Dispensar alarme") pro Android de quem tiver acesso a este veículo.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex items-end gap-3">
        <div className="flex-1 space-y-1.5">
          <Label>Veículo</Label>
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                className="w-full justify-between font-normal"
                disabled={devicesLoading}
              >
                <span className={cn("truncate", !selected && "text-muted-foreground")}>
                  {devicesLoading
                    ? "Carregando veículos…"
                    : (selected?.name ?? "Buscar veículo pelo nome…")}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
              <Command shouldFilter={false}>
                <CommandInput placeholder="Digite ao menos 2 letras…" value={search} onValueChange={setSearch} />
                <CommandList>
                  <CommandEmpty>
                    {search.trim().length < 2 ? "Digite ao menos 2 letras para buscar." : "Nenhum veículo encontrado."}
                  </CommandEmpty>
                  <CommandGroup>
                    {filtered.map((d) => (
                      <CommandItem
                        key={d.id}
                        value={String(d.id)}
                        onSelect={() => {
                          setSelected({ id: d.id, name: d.name });
                          setPickerOpen(false);
                        }}
                      >
                        {d.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <Button onClick={handleSend} disabled={sendTestPush.isPending || !selected}>
          <Send className="mr-2 h-4 w-4" />
          Enviar teste
        </Button>
      </CardContent>
    </Card>
  );
}

function SoundSlot({
  alertKey,
  label,
  emoji,
  current,
}: {
  alertKey: AlertType | typeof TECHNICIAN_ASSIGNED_SOUND_KEY | string;
  label: string;
  emoji: string;
  current?: { url: string; fileName: string };
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadNotificationSound();
  const remove = useDeleteNotificationSound();
  const [isPlaying, setIsPlaying] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      await upload.mutateAsync({ alertKey, file });
    } catch (err) {
      toast.error(err instanceof PlatformApiError ? err.message : "Falha ao enviar o áudio");
    }
  };

  const handleRemove = async () => {
    try {
      await remove.mutateAsync(alertKey);
    } catch (err) {
      toast.error(err instanceof PlatformApiError ? err.message : "Falha ao remover o áudio");
    }
  };

  const handlePlay = () => {
    if (!current) return;
    const audio = new Audio(current.url);
    setIsPlaying(true);
    audio.addEventListener("ended", () => setIsPlaying(false));
    audio.play().catch(() => setIsPlaying(false));
  };

  const isBusy = upload.isPending || remove.isPending;

  return (
    <div className="flex items-center gap-3 rounded-xl border border-border px-3 py-2.5">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-muted text-base">
        {emoji}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{label}</div>
        <p className="truncate text-xs text-muted-foreground">
          {current ? current.fileName : "Sem som customizado — nenhum efeito no app enquanto não enviar um arquivo"}
        </p>
      </div>
      {current ? (
        <Badge variant="secondary" className="shrink-0 gap-1 text-[10px]">
          <Music className="h-3 w-3" />
          Configurado
        </Badge>
      ) : null}
      {current ? (
        <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={handlePlay} disabled={isBusy}>
          {isPlaying ? "Tocando…" : "Tocar"}
        </Button>
      ) : null}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="shrink-0"
        onClick={() => inputRef.current?.click()}
        disabled={isBusy}
      >
        <Upload className="h-3.5 w-3.5" />
        {upload.isPending ? "Enviando…" : current ? "Substituir" : "Enviar"}
      </Button>
      {current ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0 text-red-600 hover:text-red-600"
          onClick={handleRemove}
          disabled={isBusy}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      ) : null}
      <input ref={inputRef} type="file" accept="audio/*" className="hidden" onChange={handleFileChange} />
    </div>
  );
}
